import React, { useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { NumericKeyboard } from "../components/common/NumericKeyboard";
import { ChevronLeft, ChevronRight, Loader2, Check, AlertCircle, Landmark, Wallet, Globe, Coins, ShieldCheck, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, cn } from "../lib/utils";
import { toast } from "sonner";
import axios from "axios";

import { useReactToPrint } from 'react-to-print';
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const transferTypes = {
  local: {
    title: "Local Transfer",
    description: "Transfer to local banks",
    icon: <Landmark className="w-5 h-5" />,
    steps: ["beneficiary", "amount", "pin"],
    accent: "bg-blue-500",
  },
  "same-bank": {
    title: "Same Bank",
    description: "Instant DigitalVault transfer",
    icon: <Wallet className="w-5 h-5" />,
    steps: ["beneficiary", "amount", "pin"],
    accent: "bg-emerald-500",
  },
  international: {
    title: "International",
    description: "Global wire transfer",
    icon: <Globe className="w-5 h-5" />,
    steps: ["beneficiary", "international", "amount", "pin"],
    accent: "bg-violet-500",
  },
  crypto: {
    title: "Crypto Send",
    description: "Move digital assets",
    icon: <Coins className="w-5 h-5" />,
    steps: ["wallet", "amount", "pin"],
    accent: "bg-amber-500",
  },
};

const banks = [
  "Chase Bank", "Bank of America", "Wells Fargo", "Citibank"
];

const cryptoNetworks = ["Ethereum", "Bitcoin", "Binance Smart Chain", "Polygon", "Solana"];

export const Transfer = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const transferConfig = transferTypes[type] || transferTypes.local;
  const steps = transferConfig.steps;

  const [formData, setFormData] = useState({
    beneficiaryName: "",
    beneficiaryBank: "",
    beneficiaryAddress: "",
    narration: "",
    amount: "",
    swiftCode: "",
    iban: "",
    imfCode: "",
    cotCode: "",
    bankCountry: "",
    walletAddress: "",
    network: "",
    pin: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const validateStep = () => {
    const step = steps[currentStep];
    if (step === "beneficiary") {
      if (!formData.beneficiaryName) {
        setError("Please enter beneficiary name");
        return false;
      }
      if (type !== "crypto" && !formData.beneficiaryBank) {
        setError("Please select a bank");
        return false;
      }
    }
    if (step === "international") {
      if (!formData.swiftCode || !formData.iban || !formData.bankCountry) {
        setError("Please fill all international transfer fields");
        return false;
      }
    }
    if (step === "wallet") {
      if (!formData.walletAddress || !formData.network) {
        setError("Please enter wallet address and select network");
        return false;
      }
    }
    if (step === "amount") {
      const amount = parseFloat(formData.amount);
      if (!amount || amount <= 0) {
        setError("Please enter a valid amount");
        return false;
      }
      if (amount > (user?.available_balance || 0)) {
        setError("Insufficient balance");
        return false;
      }
    }
    if (step === "pin") {
      if (formData.pin.length !== 4) {
        setError("Please enter your 4-digit PIN");
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    setError("");

    try {
      const payload = {
        transfer_type: type === "same-bank" ? "same_bank" : type,
        beneficiary_name: formData.beneficiaryName || `Wallet: ${formData.walletAddress}`,
        beneficiary_bank: formData.beneficiaryBank,
        beneficiary_address: formData.beneficiaryAddress,
        narration: formData.narration || `${transferConfig.title} Transfer`,
        amount: parseFloat(formData.amount),
        transaction_pin: formData.pin,
        swift_code: formData.swiftCode,
        iban: formData.iban,
        imf_code: formData.imfCode,
        cot_code: formData.cotCode,
        bank_country: formData.bankCountry,
        wallet_address: formData.walletAddress,
        network: formData.network,
      };

      

      const response = await axios.post(`${API_URL}/transfer`, payload);
      setResult(response.data);
      
      await refreshUser();
      toast.success("Transfer successful!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Transfer failed", {
  duration: 10000,
  unstyled: true, // This is key to removing the default toast look
  classNames: {
    toast: "flex items-center w-full p-4 rounded-2xl bg-red-200 border-none shadow-lg",
    title: "text-red-600 font-bold text-[12px] uppercase tracking-wider",
  },
});

      setError(err.response?.data?.detail || err.response?.data?.message || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };


    const contentRef = useRef(null);
  
    // 2. Initialize the print function
    const handlePrint = useReactToPrint({
      contentRef: contentRef, // This MUST match the ref below
      documentTitle: `Receipt_${result?.id?.slice(0, 8)}`,
    });
  

  if (result) {
    return (
      <div className="max-w-md mx-auto animate-in fade-in zoom-in duration-500 px-1" data-testid="transfer-receipt" ref={contentRef} >
        <Card className="border-none shadow-2xl overflow-hidden rounded-[2.5rem] bg-white">
          <div className={cn(
            "pt-12 pb-10 text-white text-center relative px-6",
            result.status === "success" ? "bg-slate-900" : "bg-destructive"
          )}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />
            <div className="w-20 h-20 rounded-3xl bg-white/10 flex items-center justify-center mx-auto mb-6 backdrop-blur-md border border-white/20 rotate-3">
              {result.status === "success" ? (
                <Check className="w-10 h-10 stroke-[3px]" />
              ) : (
                <AlertCircle className="w-10 h-10 stroke-[3px]" />
              )}
            </div>
            <h2 className="text-xl font-black uppercase tracking-tighter mb-2">
              {result.status === "success" ? "Success" : "Failed"}
            </h2>
            <p className="text-white/60 font-bold uppercase text-[12px] tracking-[0.2em]">
              Transaction Receipt
            </p>
          </div>
          
          <CardContent className="p-8 space-y-8">
            <div className="text-center">
              <p className="text-[12px] font-black uppercase tracking-widest text-slate-400 mb-2">Total Amount</p>
              <p className="text-3xl font-black text-slate-900 tracking-tighter">
                {formatCurrency(result.amount, user?.currency)}
              </p>
              {result.service_charge > 0 && (
                <div className="inline-flex items-center gap-2 mt-4 px-3 py-1 bg-slate-50 rounded-full border border-slate-100">
                   <p className="text-[12px] font-bold text-slate-500 uppercase tracking-tight">
                    Fee: {formatCurrency(result.service_charge, user?.currency)}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4 pt-4">
              {[
                { label: "Reference", value: result.id.slice(0, 12).toUpperCase(), mono: true },
                { label: "Recipient", value: result.beneficiary_name },
                { label: "Date", value: new Date(result.date).toLocaleString() },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0">
                  <span className="text-[12px] font-black uppercase tracking-widest text-slate-400">{item.label}</span>
                  <span className={cn("text-sm font-bold text-slate-900", item.mono && "font-mono bg-slate-50 px-2 py-0.5 rounded")}>
                    {item.value}
                  </span>
                </div>
              ))}
              <div className="pt-4 flex justify-between items-center">
                <span className="text-[12px] font-black uppercase tracking-widest text-slate-400">Balance</span>
                <span className="text-lg font-black text-emerald-600">
                  {formatCurrency(result.balance_after, user?.currency)}
                </span>
              </div>
            </div>

            <div className="space-y-3 pt-4">
              <Button
                onClick={handlePrint}
                className="w-full h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-sm font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 transition-all active:scale-95"
                data-testid="view-receipt-btn"
              >
                Download Receipt
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="w-full h-14 rounded-2xl text-[12px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900"
                data-testid="back-to-dashboard-btn"
              >
                Return to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-1" data-testid="transfer-form">
      <Card className="border-none shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] rounded-[2.5rem] bg-white overflow-hidden animate-in fade-in slide-in-from-bottom-6 duration-700">
        <CardHeader className="p-8 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className={cn("p-3 rounded-2xl text-white shadow-lg", transferConfig.accent)}>
              {transferConfig.icon}
            </div>
            <div className="px-3 py-1 bg-slate-100 rounded-full">
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">Step {currentStep + 1} of {steps.length}</span>
            </div>
          </div>
          <CardTitle className="text-xl font-black uppercase tracking-tighter text-slate-900">{transferConfig.title}</CardTitle>
          <CardDescription className="font-bold text-[11px] uppercase tracking-tight text-slate-400">{transferConfig.description}</CardDescription>
        </CardHeader>

        <CardContent className="p-8 pt-0">
          {/* Progress Bar */}
          <div className="h-1.5 w-full bg-slate-100 rounded-full mb-10 overflow-hidden">
            <div 
              className={cn("h-full transition-all duration-500 ease-out", transferConfig.accent)}
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          {error && (
            <Alert variant="destructive" className="mb-8 rounded-2xl border-none bg-red-50 text-red-600 animate-in shake duration-500">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="font-bold text-[12px] uppercase tracking-wider">{error}</AlertDescription>
            </Alert>
          )}

          <div className="min-h-[280px]">
            {/* Beneficiary Step */}
            {steps[currentStep] === "beneficiary" && (
              <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Recipient Name</Label>
                  <Input
                    className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-bold text-slate-900 focus-visible:ring-slate-900 px-5"
                    value={formData.beneficiaryName}
                    onChange={(e) => handleChange("beneficiaryName", e.target.value)}
                    placeholder="Beneficiary Name"
                    data-testid="beneficiary-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Destination Bank</Label>
                  <Input
                    className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-bold text-slate-900 focus-visible:ring-slate-900 px-5"
                    value={formData.beneficiaryBank}
                    onChange={(e) => handleChange("beneficiaryBank", e.target.value)}
                    placeholder="Beneficiary Bank"
                    data-testid="beneficiary-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Narration</Label>
                  <Input
                    className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-bold text-slate-900 focus-visible:ring-slate-900 px-5"
                    value={formData.narration}
                    onChange={(e) => handleChange("narration", e.target.value)}
                    placeholder="What is this for?"
                    data-testid="narration-input"
                  />
                </div>
              </div>
            )}

            {/* Wallet Step (Crypto) */}
            {steps[currentStep] === "wallet" && (
              <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Wallet Address</Label>
                  <Input
                    className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-mono font-bold text-slate-900 focus-visible:ring-slate-900 px-5"
                    value={formData.walletAddress}
                    onChange={(e) => handleChange("walletAddress", e.target.value)}
                    placeholder="0x..."
                    data-testid="wallet-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Network</Label>
                  <Select
                    value={formData.network}
                    onValueChange={(v) => handleChange("network", v)}
                  >
                    <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-none font-bold text-slate-900 focus:ring-slate-900 px-5" data-testid="network-select">
                      <SelectValue placeholder="Select network" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-none shadow-2xl">
                      {cryptoNetworks.map((network) => (
                        <SelectItem key={network} value={network} className="text-sm font-bold py-3">{network.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* International Step */}
            {steps[currentStep] === "international" && (
              <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-black uppercase text-slate-400 ml-1 tracking-widest">SWIFT / BIC Code</Label>
                    <Input
                      className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-mono font-bold uppercase focus-visible:ring-slate-900 px-5"
                      value={formData.swiftCode}
                      onChange={(e) => handleChange("swiftCode", e.target.value.toUpperCase())}
                      placeholder="SWIFT CODE"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-black uppercase text-slate-400 ml-1 tracking-widest">IBAN</Label>
                    <Input
                      className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-mono font-bold uppercase focus-visible:ring-slate-900 px-5"
                      value={formData.iban}
                      onChange={(e) => handleChange("iban", e.target.value.toUpperCase())}
                      placeholder="ACCOUNT NUMBER"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-bold text-slate-900 px-5" value={formData.imfCode} onChange={(e) => handleChange("imfCode", e.target.value)} placeholder="IMF CODE" />
                    <Input className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-bold text-slate-900 px-5" value={formData.cotCode} onChange={(e) => handleChange("cotCode", e.target.value)} placeholder="COT CODE" />
                  </div>
                  <Input className="h-14 text-sm rounded-2xl bg-slate-50 border-none font-bold text-slate-900 px-5" value={formData.bankCountry} onChange={(e) => handleChange("bankCountry", e.target.value)} placeholder="DESTINATION COUNTRY" />
                </div>
              </div>
            )}

            {/* Amount Step */}
            {steps[currentStep] === "amount" && (
              <div className="space-y-8 animate-in slide-in-from-right-8 duration-500">
                <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-black uppercase text-slate-400 tracking-widest mb-1">Available</p>
                    <p className="text-xl font-black text-slate-900 tracking-tight">
                      {formatCurrency(user?.available_balance || 0, user?.currency)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <Zap className="w-5 h-5 text-amber-500 fill-amber-500" />
                  </div>
                </div>
                <div className="space-y-4">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-[0.2em] block text-center">Transfer Amount</Label>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-slate-900 rounded-3xl blur-2xl opacity-0 group-focus-within:opacity-10 transition-opacity" />
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-black text-slate-300">
                      {user?.currency === 'USD' ? '$' : user?.currency || ''}
                    </span>
                    <Input
                      type="number"
                      className="h-24 pl-14 text-4xl font-black bg-slate-50 border-none rounded-3xl focus-visible:ring-slate-900 transition-all text-slate-900"
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                </div>
              </div>
            )}

            {/* PIN Step */}
            {steps[currentStep] === "pin" && (
              <div className="space-y-8 animate-in slide-in-from-right-8 duration-500">
                <div className="text-center">
                  <div className="inline-flex p-3 rounded-2xl bg-slate-50 mb-4">
                    <ShieldCheck className="w-6 h-6 text-slate-900" />
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tighter text-slate-900">Authorize</h3>
                  <p className="text-[12px] font-bold uppercase text-slate-400 tracking-widest mt-1">Enter your 4-digit security PIN</p>
                </div>
                <div className="flex justify-center gap-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        "w-14 h-16 rounded-2xl border-[3px] flex items-center justify-center text-3xl font-black transition-all duration-300",
                        formData.pin.length > i
                          ? "border-slate-900 bg-slate-900 text-white scale-105 shadow-xl shadow-slate-200"
                          : "border-slate-100 bg-slate-50 text-slate-900"
                      )}
                    >
                      {formData.pin.length > i ? "•" : ""}
                    </div>
                  ))}
                </div>
                <NumericKeyboard
                  value={formData.pin}
                  onChange={(v) => handleChange("pin", v)}
                  maxLength={4}
                />
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-12">
            {currentStep > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="w-14 h-14 rounded-2xl border-none bg-slate-50 text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-all active:scale-90"
              >
                <ChevronLeft className="w-6 h-6 stroke-[3px]" />
              </Button>
            )}
            <Button
              onClick={currentStep < steps.length - 1 ? nextStep : handleSubmit}
              className="flex-1 h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-sm font-black uppercase tracking-[0.2em] shadow-2xl shadow-slate-200 transition-all active:scale-95"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin text-sm" />
              ) : currentStep < steps.length - 1 ? (
                <>Next Step <ChevronRight className="w-4 h-4 ml-2 stroke-[3px]" /></>
              ) : (
                `Confirm ${formData.amount ? formatCurrency(formData.amount, user?.currency) : 'Transfer'}`
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <p className="text-center mt-8 text-[11px] font-black text-slate-300 uppercase tracking-widest">
        End-to-End Encrypted Transaction
      </p>
    </div>
  );
};